// A person's profile photo on their Settings profile (staff.addPhoto): pick one, it is recorded with
// its size and SHA-256, sent to the file bucket (files.createUploadUrl) and checked there
// (files.confirmUpload). Until the check passes the profile says it shows once it's checked; an
// upload that fails keeps the picked photo, so Try again sends the same one.

import { useState } from 'react';
import { client } from '../api.ts';
import { refusal } from '../history/customer-edit-model.ts';
import { newId } from '../shared/ids.ts';
import {
  confirmFailText,
  PHOTO_TYPES,
  PHOTO_WORDS,
  photoProblem,
  photoState,
  putFailText,
} from './staff-photo-model.ts';

async function sha256(file: File): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(d), (b) => b.toString(16).padStart(2, '0')).join('');
}

class UploadFailed extends Error {}

export function StaffPhoto({
  person,
  onChanged,
}: {
  person: { id: string; displayName: string; photoFileId: string | null; photoUrl: string | null };
  /** The profile changed (a new photo, its row version too): read it again. */
  onChanged: () => void;
}) {
  // Recorded but not up yet: Try again sends this file under the same id.
  const [held, setHeld] = useState<{ fileId: string; file: File } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const state = photoState(person);

  const upload = async (fileId: string, file: File) => {
    const u = await client.call<{
      upload: { url: string; method: 'PUT'; headers: Record<string, string> } | null;
    }>('files.createUploadUrl', { fileId });
    if (u.upload) {
      let put: Response;
      try {
        put = await fetch(u.upload.url, { method: 'PUT', headers: u.upload.headers, body: file });
      } catch {
        throw new UploadFailed(putFailText(null));
      }
      if (!put.ok) throw new UploadFailed(putFailText(put.status));
    }
    try {
      await client.call('files.confirmUpload', { fileId });
    } catch (e) {
      const reason = (e as { detail?: { reason?: string } }).detail?.reason;
      throw new UploadFailed(confirmFailText(reason));
    }
  };

  const send = async (fileId: string, file: File, recorded: boolean) => {
    setBusy(true);
    setError(null);
    setSaid(null);
    try {
      if (!recorded) {
        await client.call(
          'staff.addPhoto',
          {
            staffId: person.id,
            fileId,
            contentType: file.type,
            bytes: file.size,
            sha256: await sha256(file),
          },
          { idempotencyKey: fileId },
        );
        setHeld({ fileId, file });
        onChanged();
      }
      await upload(fileId, file);
      setHeld(null);
      setSaid(`${person.displayName}’s new photo is up.`);
      onChanged();
    } catch (e) {
      setError(e instanceof UploadFailed ? e.message : refusal(e));
    } finally {
      setBusy(false);
    }
  };

  const pick = (file: File | undefined) => {
    if (!file) return;
    const problem = photoProblem(file.type, file.size);
    if (problem) return setError(problem);
    // a file is hashed before it goes up, which browsers allow only on a secure page (https)
    if (!crypto.subtle)
      return setError('This page isn’t on a secure (https) connection, so a photo can’t be added here.');
    void send(newId(), file, false);
  };

  return (
    <div className="staff-photo" aria-busy={busy}>
      {state === 'shown' ? (
        <img src={person.photoUrl!} alt={`${person.displayName}’s photo`} width={72} height={72} />
      ) : (
        <span className="staff-photo-none" aria-hidden="true" />
      )}
      <div className="staff-photo-side">
        {state !== 'shown' && <p className="small muted">{PHOTO_WORDS[state]}</p>}
        <label className="btn soft small">
          {state === 'none' ? 'Add a photo' : 'Change the photo'}
          <input
            type="file"
            className="sr-file"
            accept={PHOTO_TYPES.join(',')}
            aria-label="Photo"
            disabled={busy}
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        {busy && <p className="small muted">Sending the photo…</p>}
        {error && (
          <p className="error small" role="alert">
            {error}{' '}
            {held && !busy && (
              <button
                type="button"
                className="btn quiet small"
                onClick={() => void send(held.fileId, held.file, true)}
              >
                Try again
              </button>
            )}
          </p>
        )}
        {said && (
          <p className="small" role="status">
            {said}
          </p>
        )}
      </div>
    </div>
  );
}
